import { Loader, cookieNames } from '@monorepo/common';
import { Card, Typography } from '@mui/material';
import cookies from 'js-cookie';
import { memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showDropButton } from '../../../features/sliceStepper';
import { useClearReactivateDataQuery } from '../../../services/serviceJobData';
import { IPaymentResponse } from '../../../services/servicePostAJob/responseInterfaces';
import FinalBlockItem from '../../PostJobListing/components/FinalBlockItem';
import { book } from '../../../routers/books';
import {
  getEditJobPath,
  getOriginJobSite,
  getPublicJobUrl,
} from '../../../utils/jobAccountRoutes';
import { formatOrderAmount } from '../../../utils/order';
import { gtmPush } from '../../../utils/gtm';

interface IFinalBlockProps {
  data?: IPaymentResponse;
  siteId?: number | null;
}
const FinalComponent = ({ data, siteId }:IFinalBlockProps) => {
  const dispatch = useDispatch();
  // Read the draft guid at first render, before the effect below removes the cookie
  // (same race as PostJobListing's FinalBlock — the query re-read the cookie when it
  // built the URL and lost).
  const [tempReactivateGuid] = useState<string>(() => cookies.get(cookieNames.tempReactivateJob) || '');
  const { isLoading } = useClearReactivateDataQuery({ guid: tempReactivateGuid }, {
    skip: !tempReactivateGuid
  });

  useEffect(() => {
    dispatch(showDropButton());
  }, [isLoading]);

  useEffect(() => {
    if (data) {
      // GA4 ecommerce purchase (replaces the legacy window.gtag Ads conversion,
      // D-24588). Reactivations previously only fired a gtag Ads hit; the GA4
      // purchase event now covers both GA4 revenue and the Google Ads conversion
      // (AW-1019972986), which fires from a GTM-K4WKF75L tag on this event.
      const amount = Number(data.orderAmount ?? 0);
      if (data.orderId && amount > 0) {
        gtmPush({ ecommerce: null }); // clear previous ecommerce object (GA4/GTM convention)
        gtmPush({
          event: 'purchase',
          ecommerce: {
            transaction_id: String(data.orderId),
            value: amount,
            currency: 'USD',
            items: [{ item_name: 'Job Listing Reactivation', item_category: 'cpgjobs' }]
          }
        });
      }

      cookies.remove(cookieNames.tempReactivateJob);
    }
  }, [data]);

  const listingSiteId = data?.siteId === undefined ? siteId : data.siteId;
  const publicJobUrl = getPublicJobUrl(listingSiteId, data?.path);
  const originJobSite = getOriginJobSite(listingSiteId, data?.path);
  const editJobPath = data?.eguid
    ? getEditJobPath(listingSiteId, data.eguid)
    : null;
  const amount = formatOrderAmount(data?.orderAmount);

  return (
    <>
      <Loader isShow={isLoading} />

      <Card
        sx={{
          mt: 2,
          px: 2,
          p: 2.5
        }}
        variant="outlined"
        data-cy="finalStep"
      >

        <Typography
          variant="h4"
          fontWeight="bold"
        >
          Order Complete - Thank You
        </Typography>
        <Typography
          variant="h5"
          marginTop={4}
          marginBottom={4}
        >
          Your job listing
        </Typography>

        <Typography
          variant="body1"
          marginTop={1}
          marginBottom={2}
        >
          Your job listing is now active.
        </Typography>

        {data?.orderId ? (
          <Typography variant="body1" marginBottom={1}>
            <strong>Order ID:</strong>
            {' '}
            {data.orderId}
          </Typography>
        ) : null}

        {amount ? (
          <Typography variant="body1" marginBottom={3}>
            <strong>Amount:</strong>
            {' '}
            {amount}
          </Typography>
        ) : null}

        {publicJobUrl ? (
          <FinalBlockItem
            title="VIEW YOUR JOB LISTING"
            link={publicJobUrl}
          />
        ) : null}

        {originJobSite ? (
          <FinalBlockItem
            title={`ALSO VIEW ON ${originJobSite.name.toUpperCase()}`}
            link={originJobSite.url}
          />
        ) : null}

        {editJobPath ? (
          <FinalBlockItem
            isLocal
            title="EDIT YOUR JOB LISTING"
            link={editJobPath}
          />
        ) : null}

        {data?.orderGuid ? (
          <FinalBlockItem
            title="VIEW ORDER"
            link={`https://app.bevnet.com/orders/receipt/${data.orderGuid}`}
          />
        ) : null}

        <FinalBlockItem
          isLocal
          title="MANAGE JOB LISTINGS"
          link={book.jobsListings}
        />

        <Typography
          variant="body1"
          marginTop={4}
          color="#292929"
        >
          <strong>IMPORTANT: </strong>
          {' '}
          These links -- and a receipt for your purchase -- have also been sent to you via email.
        </Typography>

      </Card>
    </>
  );
};
export default memo(FinalComponent);
